// `GET /api/v1/inbox`: what is waiting for the signed-in user to answer, across every
// context they hold, grouped by workspace (#495, #496; `control_plane/routes/inbox.py`).
// Each group also carries its answered items, newest first, a page at a time (#497), and
// `GET /api/v1/inbox/answered` reads one group's next page.
//
// A session and no context, as `GET /invites/waiting` is. The inbox answers nothing
// itself: an invitation is accepted by its own route, and a consent request, capability
// request, change approval or held alarm (#849) is answered on its own screen, so this module only reads.

import type { HeldAlarm } from "./automation";
import type { ChangeHolder } from "./changes";
import type { Api } from "./http";
import type { WaitingInvitation } from "./invites";

export const INBOX_PATH = "/api/v1/inbox";
export const INBOX_ANSWERED_PATH = "/api/v1/inbox/answered";

export type InboxKind = "invitation" | "consent_request" | "capability_request" | "change_approval" | "held_alert";

interface ItemBase {
  id: string;
  /** Set for an item addressed to a person; null for one addressed to a role. */
  email: string | null;
  workspace_id: string | null;
  org_id: string | null;
  /** The permission its answerer holds; null for an item addressed to a person. */
  permission: string | null;
  workspace_name: string | null;
  requested_at: string | null;
  expires_at: string | null;
}

export interface InvitationItem extends ItemBase {
  kind: "invitation";
  /** `WaitingInvitation`'s own shape, so the row and its accept are unit 1's. */
  detail: WaitingInvitation;
}

export interface ConsentItem extends ItemBase {
  kind: "consent_request";
  detail: { run_id: string; resource_arn: string; operations: string[]; reason: string };
}

export interface CapabilityItem extends ItemBase {
  kind: "capability_request";
  detail: {
    capability: string;
    requester_org_id: string;
    requester: string | null;
    requester_kind: string | null;
    requested_by: string | null;
  };
}

export interface ChangeItem extends ItemBase {
  kind: "change_approval";
  detail: {
    run_id: string;
    title: string | null;
    risk: string | null;
    aws_account_id: string | null;
    /** `workspace`, or `grant` when an organisation holds `approve_writes` on the workspace. */
    authority: "workspace" | "grant";
    /** The side that answers, and whose context the changes screen reads it under. */
    holder: ChangeHolder;
  };
}

/** An alarm the daily cap on automatic investigations held for a person (#849). */
export interface HeldAlertItem extends ItemBase {
  kind: "held_alert";
  detail: Omit<HeldAlarm, "id" | "held_at">;
}

export type InboxItem = InvitationItem | ConsentItem | CapabilityItem | ChangeItem | HeldAlertItem;

/**
 * How a settled item settled (#497). `outcome` and `status` are the item's own row: a change
 * the executor has moved past `approved` reads `approved` with its status beside it. `by` and
 * `at` are the audit row's, and null when there is none: an expired item, or one whose audit
 * row has been retired.
 */
export interface Answer {
  outcome: string;
  status: string;
  by: string | null;
  at: string | null;
}

/** An invitation the person accepted: the role it gave and who sent it. */
export interface AcceptedInvitationItem extends ItemBase {
  kind: "invitation";
  detail: { role: string; invited_by: string | null };
}

export type AnsweredItem = (AcceptedInvitationItem | ConsentItem | CapabilityItem | ChangeItem | HeldAlertItem) & {
  answer: Answer;
};

export interface InboxGroup {
  kind: "workspace" | "org";
  workspace_id: string | null;
  org_id: string | null;
  name: string | null;
  org_name: string | null;
  /** The soonest deadline first, and the undated last: the route's order, kept. */
  items: InboxItem[];
  /** Newest first, one page of them: the route's order, kept. */
  answered: AnsweredItem[];
  /** The cursor to the next, older page, or null when this is the last. */
  answered_next: string | null;
}

export interface AnsweredPage {
  answered: AnsweredItem[];
  answered_next: string | null;
}

export interface InboxRead {
  count: number;
  /** The most pressing workspace first: the route's order, kept. */
  groups: InboxGroup[];
}

const KINDS: readonly string[] = ["invitation", "consent_request", "capability_request", "change_approval", "held_alert"];

/** Keeps the route's order, and drops an answered item of a kind this build does not draw. */
function asAnswered(value: unknown): AnsweredItem[] {
  return (Array.isArray(value) ? value : []).filter(
    (item): item is AnsweredItem =>
      typeof item === "object" &&
      item !== null &&
      KINDS.includes((item as AnsweredItem).kind) &&
      typeof (item as AnsweredItem).answer === "object" &&
      (item as AnsweredItem).answer !== null,
  );
}

const asCursor = (value: unknown): string | null => (typeof value === "string" && value !== "" ? value : null);

/** Keeps the route's order, and drops an item of a kind this build does not draw. */
function asInbox(value: unknown): InboxRead {
  const body = (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;
  const groups = (Array.isArray(body.groups) ? body.groups : [])
    .filter((g): g is InboxGroup => typeof g === "object" && g !== null && Array.isArray((g as InboxGroup).items))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => KINDS.includes(item.kind)),
      answered: asAnswered(group.answered),
      answered_next: asCursor(group.answered_next),
    }))
    .filter((group) => group.items.length > 0 || group.answered.length > 0);
  return { count: groups.reduce((n, g) => n + g.items.length, 0), groups };
}

export const readInbox = async (api: Api): Promise<InboxRead> => asInbox(await api.get<unknown>(INBOX_PATH));

/** The key a group is paged by: `workspace:<id>` or `org:<id>`. */
export const groupKeyOf = (group: Pick<InboxGroup, "kind" | "workspace_id" | "org_id">): string =>
  `${group.kind}:${group.kind === "workspace" ? group.workspace_id : group.org_id}`;

/** One group's next page of answered items, older than `cursor`. */
export async function readOlderAnswered(api: Api, group: string, cursor: string): Promise<AnsweredPage> {
  const query = new URLSearchParams({ group, before: cursor });
  const body = (await api.get<unknown>(`${INBOX_ANSWERED_PATH}?${query.toString()}`)) as Record<string, unknown> | null;
  return { answered: asAnswered(body?.answered), answered_next: asCursor(body?.answered_next) };
}

